import * as React from 'react';
import { XIcon } from '@phosphor-icons/react';
import { cn } from '@/utils/utils';
import { DISABLED, FOCUS } from '@/components/ui/recipes';
import { IconButton } from '@/components/ui/icon-button';
import { WithTooltip } from '@/components/ui/tooltip';

interface ChipProps {
  label: React.ReactNode;
  selected?: boolean;
  onClick?: () => void;
  /** Makes it a removable tag: an x after the label that calls this, with the x's click. */
  onRemove?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** The x's accessible name; Remove and the label by default. */
  removeLabel?: string;
  /** A glyph before the label, drawn h-3.5 w-3.5. */
  icon?: React.ReactNode;
  /** What it says on hover, as a themed tooltip. */
  title?: string;
  disabled?: boolean;
  /** sm in filter rows and pickers; xs inside a field. */
  size?: 'sm' | 'xs';
  className?: string;
}

const SIZES = {
  sm: 'gap-1.5 px-2.5 py-1 leading-tight text-lureo-12 [&_svg]:h-3.5 [&_svg]:w-3.5',
  xs: 'h-5 gap-1 px-2 text-lureo-11 [&_svg]:h-3 [&_svg]:w-3',
} as const;

/** Lureo's chip: a filter, a choice or a tag shown as a small pill, selected when on. */
export function Chip({ label, selected = false, onClick, onRemove, removeLabel, icon, title, disabled, size = 'sm', className }: ChipProps) {
  const look = cn(
    'inline-flex min-w-0 shrink-0 items-center rounded-lureo-radius-chip border-lureo-chip-line-width line-style-lureo-line-style font-lureo-font-ui leading-none transition-colors',
    SIZES[size],
    selected ? 'border-lureo-chip-on-line bg-lureo-chip-on font-medium text-lureo-on-chip-on hover:bg-lureo-chip-on-hover' : 'border-lureo-chip-line bg-lureo-chip text-lureo-on-chip',
    className,
  );
  if (onRemove) {
    return (
      <WithTooltip content={title}>
        <span className={cn(look, 'pr-1')}>
          {icon}
          <span className="min-w-0 truncate">{label}</span>
          <IconButton place="tag" label={removeLabel ?? `Remove ${typeof label === 'string' ? label : ''}`.trim()} onClick={onRemove} disabled={disabled}>
            <XIcon weight="bold" />
          </IconButton>
        </span>
      </WithTooltip>
    );
  }
  return (
    <WithTooltip content={title}>
      <button type="button" onClick={onClick} disabled={disabled} aria-pressed={selected} className={cn(look, FOCUS, DISABLED, !selected && 'hover:border-lureo-chip-hover-line hover:bg-lureo-chip-hover hover:text-lureo-on-chip-hover')}>
        {icon}
        {label}
      </button>
    </WithTooltip>
  );
}
